import React, { useEffect, useRef, useState } from "react";
import { api, resolveImageUrl, AVAILABILITY } from "@/lib/api";
import { PRODUCT } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Plus, Upload, Trash2, Sparkles } from "lucide-react";
import BroadcastDialog from "@/components/saheli/BroadcastDialog";
import WeaverTimeline from "@/components/saheli/WeaverTimeline";

const FABRICS = [
  "Banarasi Silk",
  "Kanjivaram",
  "Chanderi",
  "Tussar Silk",
  "Cotton",
  "Georgette",
  "Chiffon",
  "Linen",
];

const AVAIL_STYLES = {
  "In Stock": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Sold: "bg-stone-100 text-stone-500 border-stone-200",
  "Made-to-order": "bg-amber-50 text-amber-700 border-amber-200",
};

function CreateProductDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    fabric: FABRICS[0],
    price: "",
    availability: "In Stock",
    photo_url: "",
    color_tags: "",
    motif_tags: "",
    description: "",
  });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("Photo uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const submit = async () => {
    if (!form.name || !form.photo_url || !form.price) {
      toast.error("Name, photo and price are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/products", {
        name: form.name,
        fabric: form.fabric,
        price: Number(form.price),
        availability: form.availability,
        photo_url: form.photo_url,
        color_tags: form.color_tags
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        motif_tags: form.motif_tags
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        description: form.description,
      });
      toast.success("Saree added — AI is extracting features");
      setOpen(false);
      onCreated?.();
      setForm({
        name: "",
        fabric: FABRICS[0],
        price: "",
        availability: "In Stock",
        photo_url: "",
        color_tags: "",
        motif_tags: "",
        description: "",
      });
    } catch {
      toast.error("Failed to save saree");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid={PRODUCT.addButton}
          className="bg-terracotta hover:bg-terracotta-dark rounded-full"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add Saree
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={PRODUCT.createDialog} className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Add a saree to the catalog
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            {form.photo_url ? (
              <img
                src={resolveImageUrl(form.photo_url)}
                alt=""
                className="w-24 h-24 rounded-xl border border-stone-200 object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl border border-dashed border-stone-300 grid place-items-center text-stone-400">
                <Upload className="w-5 h-5" />
              </div>
            )}
            <div className="flex-1 space-y-2">
              <Input
                data-testid={PRODUCT.createPhotoUrl}
                placeholder="Paste image URL"
                value={form.photo_url?.startsWith("http") ? form.photo_url : ""}
                onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
              />
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={uploadImage}
                  data-testid={PRODUCT.createPhotoUpload}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => fileRef.current?.click()}
                >
                  <Upload className="w-3.5 h-3.5 mr-1" /> Upload file
                </Button>
              </div>
            </div>
          </div>

          <Input
            data-testid={PRODUCT.createName}
            placeholder="Saree name (e.g. Katan Bridal Red)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div className="grid grid-cols-3 gap-3">
            <Select
              value={form.fabric}
              onValueChange={(v) => setForm({ ...form, fabric: v })}
            >
              <SelectTrigger data-testid={PRODUCT.createFabric}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FABRICS.map((f) => (
                  <SelectItem key={f} value={f}>
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              data-testid={PRODUCT.createPrice}
              placeholder="Price (₹)"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              type="number"
            />
            <Select
              value={form.availability}
              onValueChange={(v) => setForm({ ...form, availability: v })}
            >
              <SelectTrigger data-testid={PRODUCT.createAvailability}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AVAILABILITY.map((a) => (
                  <SelectItem key={a} value={a}>
                    {a}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input
            data-testid={PRODUCT.createColorTags}
            placeholder="Colors (comma-separated: red, gold)"
            value={form.color_tags}
            onChange={(e) => setForm({ ...form, color_tags: e.target.value })}
          />
          <Input
            data-testid={PRODUCT.createMotifTags}
            placeholder="Motifs (comma-separated: kadwa, jaal, buti)"
            value={form.motif_tags}
            onChange={(e) => setForm({ ...form, motif_tags: e.target.value })}
          />
          <Textarea
            placeholder="Notes / craft description (optional)"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            data-testid={PRODUCT.createSubmit}
            disabled={busy}
            className="bg-terracotta hover:bg-terracotta-dark"
            onClick={submit}
          >
            {busy ? "Saving…" : "Save saree"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (filter !== "all") params.availability = filter;
      const { data } = await api.get("/products", { params });
      setItems(data || []);
    } catch (err) {
      console.warn('Saheli: request failed', err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const remove = async (id) => {
    if (!window.confirm("Remove this saree?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const seedStarter = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/products/seed-starter");
      if (data.queued > 0) {
        toast.success(
          `Seeding ${data.queued} curated sarees — refresh in ~90 seconds`,
          { duration: 5000 },
        );
        // poll for arrivals
        let tries = 0;
        const poll = setInterval(async () => {
          await load();
          tries += 1;
          if (tries > 12) clearInterval(poll);
        }, 8000);
      } else {
        toast("Starter catalog already seeded");
      }
    } catch {
      toast.error("Failed to start seeding");
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline">Catalog</div>
          <h1 className="font-serif text-4xl text-stone-900 mt-1">Saree collection</h1>
          <p className="text-stone-500 text-sm mt-2">
            {items.length} pieces · powers Saheli Match visual search.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All availability</SelectItem>
              {AVAILABILITY.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {items.length < 5 && (
            <Button
              data-testid={PRODUCT.seedButton}
              variant="outline"
              onClick={seedStarter}
              disabled={seeding}
              className="rounded-full border-terracotta/30 text-terracotta hover:bg-terracotta/5"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              {seeding ? "Seeding…" : "Seed 15 starter sarees"}
            </Button>
          )}
          <CreateProductDialog onCreated={load} />
        </div>
      </div>

      {items.length === 0 ? (
        <div data-testid={PRODUCT.grid} className="paper-card p-12 text-center">
          <div className="font-serif text-2xl text-stone-800 mb-1">
            The loom is empty.
          </div>
          <div className="text-sm text-stone-500">
            Add sarees to power Saheli Match — the visual search tool.
          </div>
        </div>
      ) : (
        <div
          data-testid={PRODUCT.grid}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        >
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={PRODUCT.card(p.id)}
              className="paper-card overflow-hidden group hover:-translate-y-1 hover:border-stone-300 transition-all duration-300"
            >
              <div className="aspect-[3/4] bg-stone-100 overflow-hidden relative">
                <img
                  src={resolveImageUrl(p.photo_url)}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <span
                  className={`absolute top-2 right-2 heritage-badge ${
                    AVAIL_STYLES[p.availability] || ""
                  }`}
                >
                  {p.availability}
                </span>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-stone-900 truncate">
                      {p.name}
                    </div>
                    <div className="text-xs text-stone-500">{p.fabric}</div>
                  </div>
                  <div className="text-terracotta font-medium">
                    ₹{Number(p.price).toLocaleString("en-IN")}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(p.color_tags || []).slice(0, 3).map((c) => (
                    <span key={c} className="heritage-badge">
                      {c}
                    </span>
                  ))}
                  {(p.motif_tags || []).slice(0, 2).map((m) => (
                    <span
                      key={m}
                      className="heritage-badge bg-terracotta/5 text-terracotta border-terracotta/20"
                    >
                      {m}
                    </span>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 truncate">
                    {p.features?.style || "—"}
                  </div>
                  <div className="flex items-center gap-3">
                    <BroadcastDialog product={p} />
                    <button
                      onClick={() => remove(p.id)}
                      className="text-stone-400 hover:text-terracotta transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {p.availability === "Made-to-order" && (
                  <WeaverTimeline product={p} onUpdated={load} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
